import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, Empty, Tag } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { aiApi, type AiDuplicateSideDto, type AiDuplicatesDto, type AiStaleCheckDto } from '../lib/wikiApi';
import { duplicateSignalChips, duplicateStatusLabel, duplicateVerdictChip, duplicatesCaption } from '../lib/duplicate-chips';
import { AiSettingsSection } from './AiSettingsSection';

/** Статья в паре дублей: ссылка и пометка «Черновик» / «На проверке» (опубликованные не помечаем). */
function DuplicateSide({ side }: { side: AiDuplicateSideDto }): JSX.Element {
  const status = duplicateStatusLabel(side.status);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <Link to={`/article/${side.slug}`}>{side.title}</Link>
      {status && <Tag style={{ margin: 0 }}>{status}</Tag>}
    </span>
  );
}

/** Вкладка «ИИ» на /admin/wiki: настройки ИИ (05.10.2026) + проверки дублей и актуальности (фаза 4.4). */
export function AiChecksTab(): JSX.Element {
  const [dups, setDups] = useState<AiDuplicatesDto | null>(null);
  const [stale, setStale] = useState<AiStaleCheckDto | null>(null);
  const dupMut = useMutation({ mutationFn: () => aiApi.duplicates(), onSuccess: setDups });
  const staleMut = useMutation({ mutationFn: () => aiApi.staleCheck(), onSuccess: setStale });

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <AiSettingsSection />
      <Alert type="info" showIcon message="Проверки выполняются по кнопке и могут занять до минуты. Дубли ищутся по заголовкам, тексту, общим файлам и смыслу; без настроенного AI-провайдера вердикта «дубль / разные» не будет, противоречия не проверяются." />

      <section style={{ border: '1px solid var(--border, #e8e6f0)', borderRadius: 8, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <b style={{ fontSize: 15 }}>Дубли статей</b>
          <Button size="small" type="primary" loading={dupMut.isPending} onClick={() => dupMut.mutate()}>Найти дубли</Button>
        </div>
        {dupMut.error && <Alert type="error" showIcon message={(dupMut.error as Error).message} />}
        {dups && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
            {duplicatesCaption(dups, dups.pairs.length)}
            {typeof dups.articles === 'number' ? ` · статей в поиске: ${dups.articles}` : ''}
          </div>
        )}
        {dups && dups.pairs.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Дублей не нашли" />}
        {dups?.pairs.map((p) => {
          const verdict = duplicateVerdictChip(p.verdict ?? (p.aiDuplicate === true ? 'same' : p.aiDuplicate === false ? 'different' : null));
          const chips = duplicateSignalChips(p.signals);
          const categories = [p.a.category, p.b.category];
          return (
            <div key={p.a.id + p.b.id} style={{ padding: '8px 0', borderTop: '1px solid var(--border, #e8e6f0)', fontSize: 13 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <DuplicateSide side={p.a} />
                <span style={{ color: 'var(--text-muted)' }}>↔</span>
                <DuplicateSide side={p.b} />
                {verdict && <Tag color={verdict.color} style={{ margin: 0 }}>{verdict.label}</Tag>}
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>схожесть {Math.round(p.similarity * 100)} %</span>
              </div>
              {chips.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                  {chips.map((c) => (
                    <Tag key={c.key} color={c.tone === 'default' ? undefined : c.tone} style={{ margin: 0 }}>{c.label}</Tag>
                  ))}
                </div>
              )}
              {categories.some(Boolean) && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {categories[0] === categories[1] ? categories[0] : categories.map((c) => c ?? 'без категории').join(' ↔ ')}
                </div>
              )}
              {p.aiReason && <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{p.aiReason}</div>}
            </div>
          );
        })}
      </section>

      <section style={{ border: '1px solid var(--border, #e8e6f0)', borderRadius: 8, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <b style={{ fontSize: 15 }}>Актуальность и противоречия</b>
          <Button size="small" type="primary" loading={staleMut.isPending} onClick={() => staleMut.mutate()}>Проверить</Button>
          {stale && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>пар стандартов проверено: {stale.checkedPairs}{stale.aiUsed ? '' : ' (AI выключен)'}</span>}
        </div>
        {staleMut.error && <Alert type="error" showIcon message={(staleMut.error as Error).message} />}
        {stale && (
          <>
            <div style={{ fontWeight: 600, margin: '6px 0' }}>Просрочена проверка: {stale.overdue.length}</div>
            {stale.overdue.slice(0, 20).map((r) => (
              <div key={r.id} style={{ fontSize: 13, padding: '3px 0' }}>
                <Link to={`/article/${r.slug}`}>{r.title}</Link>
                <span style={{ color: 'var(--text-muted)' }}> · {r.ownerName ?? 'без владельца'}{r.daysLeft != null ? ` · ${-r.daysLeft} дн. назад` : ''}</span>
              </div>
            ))}
            {stale.overdue.length > 20 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>… ещё {stale.overdue.length - 20}, полный список — вкладка «Актуальность»</div>}
            <div style={{ fontWeight: 600, margin: '12px 0 6px' }}>Противоречия между стандартами: {stale.conflicts.length}</div>
            {stale.conflicts.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Не найдено</div>}
            {stale.conflicts.map((c, i) => (
              <div key={i} style={{ fontSize: 13, padding: '6px 0', borderTop: '1px solid var(--border, #e8e6f0)' }}>
                <Link to={`/article/${c.a.slug}`}>{c.a.title}</Link> ↔ <Link to={`/article/${c.b.slug}`}>{c.b.title}</Link> <Tag>{c.category}</Tag>
                <div style={{ color: 'var(--fk-error, #E53935)' }}>{c.details}</div>
              </div>
            ))}
          </>
        )}
      </section>
    </div>
  );
}
